/**
 * Utility function to fetch AQI (Air Quality Index), temperature, and UV index data
 * @param {string} sessionId - Session ID for storing data
 * @returns {Promise<Object|null>} AQI data object or null if failed
 */
import { getApiBaseUrl } from './apiConfig';
import { fetchWithTimeout } from './fetchWithTimeout';

export const fetchAQIData = async (sessionId = null) => {
  // Only fetch if geolocation is supported
  if (!navigator.geolocation) {
    return null;
  }

  const API_BASE_URL = (getApiBaseUrl() || '').replace(/\/$/, '');

  try {
    // Get user's location
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;

    // Fetch AQI data from backend
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/aqi?latitude=${latitude}&longitude=${longitude}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
        ,
        timeoutMs: 20000,
      }
    );

    const data = await response.json().catch(() => ({}));

    if (response.ok && data.success) {
      // Include lat/lon for storage in skin_environment
      data.latitude = latitude;
      data.longitude = longitude;
      // Store AQI data in localStorage with session ID if provided
      if (sessionId) {
        try {
          localStorage.setItem(`skin_aqi_${sessionId}`, JSON.stringify(data));
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      return data;
    }

    return null;
  } catch (error) {
    // Silently fail - AQI is optional
    console.warn('Failed to fetch AQI data:', error);
    return null;
  }
};

